import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";
import usePageLanguage from "../../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../../content/pages/registry.js";
import useDocumentMeta from "../../hooks/useDocumentMeta.js";
import usePageShell from "../../hooks/usePageShell.js";
// Shared with scripts/build-static-pages.mjs, which renders the same page
// into the static document a crawler receives. See the module for why.
import styles from "./legalStyles.js";

function renderContent(block, idx) {
  if (typeof block === "string") {
    return (
      <p key={idx} style={styles.paragraph}>
        {block}
      </p>
    );
  }

  if (block.type === "emphasis") {
    return (
      <p key={idx} style={styles.emphasis}>
        {block.text}
      </p>
    );
  }

  if (block.type === "list") {
    return (
      <ul key={idx} style={styles.list}>
        {block.items.map((item, i) => (
          <li key={i} style={styles.listItem}>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "table") {
    return (
      <div key={idx} style={{ overflowX: "auto", margin: "16px 0 20px 0" }}>
        <table
          style={{ ...styles.table, display: "table" }}
          role="table"
        >
          <thead>
            <tr>
              {block.headers.map((h, i) => (
                <th key={i} style={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} style={styles.td}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

const LegalPageLayout = ({ contentEN, contentGR, titleKey, descriptionKey, path }) => {
  const { t } = useTranslation();

  // The URL decides, where it says anything at all. `shortLanguage` alone would
  // let a stored preference put English text under a `/el/` URL — the one thing
  // a localized URL must never do.
  const language = usePageLanguage();
  const content = language === "el" ? contentGR : contentEN;

  // Self-referencing, and the page's own — never index.html's, and never the
  // English URL from the Greek page. Each of these four now has a real static
  // document per language; before that they were served index.html through
  // vercel.json's catch-all and inherited the HOMEPAGE's canonical, which is
  // what this originally existed to correct. It still runs for a client-side
  // arrival, where no static document is fetched at all.
  useDocumentMeta({
    titleKey,
    descriptionKey,
    canonical: canonicalUrlFor(path, language),
    alternates: alternatesFor(path),
    robots: "index, follow",
  });

  usePageShell();

  return (
    <div className="page-wrapper legal-page" style={styles.pageWrapper}>
      <style>{`
        .legal-page .section.foot { padding-top: 60px; }
        @media (max-width: 767px) {
          .legal-page .main.legal-main { padding-top: 70px !important; }
          .legal-page .legal-container { padding-top: 24px !important; }
        }
      `}</style>
      <Navbar />
      <div className="main legal-main" style={styles.main}>
        <div className="legal-container" style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>{content.title}</h1>
            <p style={styles.date}>
              {t("legal.lastUpdated")}: {content.effectiveDate}
            </p>
          </header>

          <p style={styles.intro}>{content.intro}</p>

          {content.sections.map((section, si) => (
            <section key={si} style={styles.section}>
              <h2 style={styles.sectionHeading}>{section.heading}</h2>
              {section.content.map((block, bi) =>
                renderContent(block, `${si}-${bi}`),
              )}
            </section>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LegalPageLayout;
